import { StateCreator } from 'zustand';

// Actions
import { getAllUsers, getUserProfile } from '@/actions/client/user';

// Types
import { UsersSlice, UsersState } from './types';
import { UserProfile } from '@/types';

// Constants
import { UserRole } from '@shared/constants';
import { getUserRole } from '@/utils';

const initialUsersState: UsersState = {
  users: [],
  isLoading: false,
  hasUserProfile: false,
  isAllUsersFetched: false,
  currentRole: undefined,
};

export const createUsersSlice: StateCreator<UsersSlice, [], [], UsersSlice> = (
  set,
  get,
) => ({
  ...initialUsersState,

  setUsers: (users) => set({ users }),
  setLoading: (isLoading) => set({ isLoading }),
  setHasUserProfile: (hasUserProfile) => set({ hasUserProfile }),
  setAllUsersFetched: (fetched) => set({ isAllUsersFetched: fetched }),

  // WebSocket update handlers
  updateUser: (updatedUser: UserProfile) => {
    const { users } = get();
    const updatedUsers = users.map((user) =>
      user.id === updatedUser.id ? updatedUser : user,
    );
    set({ users: updatedUsers });
  },

  removeUser: (userId: string) => {
    const { users } = get();
    const filteredUsers = users.filter((user) => user.id !== userId);
    set({ users: filteredUsers });
  },

  addUser: (newUser: UserProfile) => {
    const { users } = get();
    const userExists = users.some((user) => user.id === newUser.id);

    if (!userExists) {
      set({ users: [...users, newUser] });
    }
  },

  fetchUsers: async ({ requestConfig, force } = {}) => {
    const { isLoading, hasUserProfile, users, currentRole } = get();
    const userRole = getUserRole();

    // Skip if already loading
    if (isLoading) return;

    // Determine if we need to fetch data
    const shouldFetch =
      // Force refresh
      (force && userRole === UserRole.ADMIN) ||
      // Not fetched before
      !hasUserProfile ||
      // Has isFetched flag but no data
      (hasUserProfile && users.length === 0) ||
      // Data role mismatch - current data is for different role
      (currentRole && currentRole !== userRole);

    if (!shouldFetch) return;

    set({ isLoading: true });

    try {
      // Always get user profile first to determine actual role from server
      const userProfileResponse = await getUserProfile({ requestConfig });
      const userProfile = userProfileResponse?.data;

      if (!userProfile) {
        set({ isLoading: false });
        return;
      }

      const { role: serverRole } = userProfile;
      const { isAllUsersFetched } = get();

      const allUsersResponse =
        serverRole === UserRole.ADMIN && (force || !isAllUsersFetched)
          ? await getAllUsers({ requestConfig })
          : null;

      let usersData: typeof users;
      let allUsersFetched = isAllUsersFetched;

      if (serverRole === UserRole.ADMIN) {
        usersData = allUsersResponse?.data ?? users;
        allUsersFetched = force
          ? true
          : isAllUsersFetched || !!allUsersResponse?.data;
      } else {
        usersData = [userProfile];
      }

      set({
        users: usersData,
        isLoading: false,
        hasUserProfile: true,
        isAllUsersFetched: allUsersFetched,
        currentRole: serverRole,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  resetUsers: () => set(initialUsersState),
});
